import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

const FACETS_SIDEBAR_CLOSED_CLASS = 'facets-sidebar-closed';
const FACETS_SIDEBAR_ID = 'facets-sidebar';
const FILTERS_DRAWER_ID = 'filters-drawer';

/**
 * @param {string} value
 * @returns {string}
 */
function decodeFilterParamValue(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Matches filter values 1:1 or after URL decoding (configured values may be
 * stored URL-encoded as in the collection filter query string).
 *
 * @param {string} activeValue
 * @param {string} configuredValue
 * @returns {boolean}
 */
function paramValuesMatch(activeValue, configuredValue) {
  if (activeValue === configuredValue) return true;

  return (
    decodeFilterParamValue(activeValue) === decodeFilterParamValue(configuredValue)
  );
}

/**
 * @param {URLSearchParams} searchParams
 * @param {string} paramName
 * @param {string} paramValue
 * @returns {boolean}
 */
function hasFilterParam(searchParams, paramName, paramValue) {
  const values = searchParams.getAll(paramName);

  return values.some((value) => paramValuesMatch(value, paramValue));
}

/**
 * @param {URLSearchParams} searchParams
 * @param {string} paramName
 * @param {string} paramValue
 * @returns {URLSearchParams}
 */
function toggleFilterParam(searchParams, paramName, paramValue) {
  const next = new URLSearchParams();

  for (const [key, value] of searchParams.entries()) {
    if (key === 'page') continue;

    if (key === paramName && paramValuesMatch(value, paramValue)) {
      continue;
    }

    next.append(key, value);
  }

  if (!hasFilterParam(searchParams, paramName, paramValue)) {
    next.append(paramName, decodeFilterParamValue(paramValue));
  }

  return next;
}

/**
 * @param {URLSearchParams} searchParams
 * @param {HTMLAnchorElement} link
 */
function syncButtonActiveState(searchParams, link) {
  const paramName = link.dataset.paramName;
  const paramValue = link.dataset.paramValue;

  if (!paramName || !paramValue) {
    link.setAttribute('aria-pressed', 'false');
    return;
  }

  const isActive = hasFilterParam(searchParams, paramName, paramValue);
  link.setAttribute('aria-pressed', isActive ? 'true' : 'false');
}

/**
 * Popular filter chips that toggle collection facet params via the existing
 * facets form Section Rendering flow. Markup uses real links for indexability;
 * primary clicks are intercepted for additive AJAX filtering.
 *
 * Also owns the filter/sort CTA (desktop sidebar / mobile drawer), optional
 * toolbar-end grid density or sort select, and forwards grid-density changes
 * to results-list.
 *
 * @extends {Component}
 */
class PopularFiltersComponent extends Component {
  /** @type {HTMLAnchorElement[]} */
  #buttons = [];

  /** @type {HTMLButtonElement | null} */
  #filterToggle = null;

  connectedCallback() {
    super.connectedCallback();
    this.#buttons = [...this.querySelectorAll('.popular-filters__button')].filter(
      (el) => el instanceof HTMLAnchorElement
    );
    const toggle = this.querySelector('.popular-filters__filter-toggle');
    this.#filterToggle = toggle instanceof HTMLButtonElement ? toggle : null;

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleGridChange);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);

    this.#syncFilterToggleUI();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleGridChange);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const filterToggle = target.closest('.popular-filters__filter-toggle');
    if (filterToggle instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#handleFilterToggleClick();
      return;
    }

    const link = target.closest('.popular-filters__button');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.getAttribute('aria-disabled') === 'true' || !link.href) return;

    // Allow open-in-new-tab / modified clicks to follow the indexable href.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    const paramName = link.dataset.paramName;
    const paramValue = link.dataset.paramValue;
    if (!paramName || !paramValue) return;

    const facetsForm = document.querySelector('facets-form-component');
    if (
      !(facetsForm instanceof HTMLElement) ||
      typeof /** @type {{ updateFiltersByURL?: unknown }} */ (/** @type {unknown} */ (facetsForm))
        .updateFiltersByURL !== 'function'
    ) {
      return;
    }

    event.preventDefault();

    const url = new URL(window.location.href);
    const nextParams = toggleFilterParam(url.searchParams, paramName, paramValue);
    url.search = nextParams.toString();

    /** @type {{ updateFiltersByURL: (url: string) => void }} */ (
      /** @type {unknown} */ (facetsForm)
    ).updateFiltersByURL(url.toString());
  };

  #handleFilterToggleClick = () => {
    if (isMobileBreakpoint()) {
      this.#openFiltersDrawer();
      return;
    }

    document.documentElement.classList.toggle(FACETS_SIDEBAR_CLOSED_CLASS);
    this.#syncFilterToggleUI();
  };

  #openFiltersDrawer = () => {
    const drawer = document.getElementById(FILTERS_DRAWER_ID);
    if (
      !(drawer instanceof HTMLElement) ||
      typeof /** @type {{ showDialog?: unknown }} */ (/** @type {unknown} */ (drawer)).showDialog !==
        'function'
    ) {
      return;
    }

    /** @type {{ showDialog: () => void }} */ (/** @type {unknown} */ (drawer)).showDialog();
  };

  /**
   * Forward density control changes to results-list (controls live outside it).
   *
   * @param {Event} event
   */
  #handleGridChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== 'radio') return;
    if (!target.closest('.popular-filters__toolbar-end')) return;

    const resultsList = document.querySelector('results-list');
    if (
      !(resultsList instanceof HTMLElement) ||
      typeof /** @type {{ updateLayout?: unknown }} */ (/** @type {unknown} */ (resultsList))
        .updateLayout !== 'function'
    ) {
      return;
    }

    /** @type {{ updateLayout: (event: { target: EventTarget | null }) => void }} */ (
      /** @type {unknown} */ (resultsList)
    ).updateLayout({ target });
  };

  /**
   * Prefer location.search: updateFiltersByURL pushStates before dispatching
   * FilterUpdateEvent with form-derived params that may still be stale.
   *
   * @param {Event} _event
   */
  #handleFilterUpdate = (_event) => {
    const queryParams = new URLSearchParams(window.location.search);

    for (const button of this.#buttons) {
      syncButtonActiveState(queryParams, button);
    }

    this.#syncSortSelect(queryParams);
    this.#syncFilterToggleUI();
  };

  /**
   * Keep toolbar sort select in sync when sort changes elsewhere (e.g. sidebar).
   *
   * @param {URLSearchParams} searchParams
   */
  #syncSortSelect = (searchParams) => {
    const select = this.querySelector('.popular-filters__toolbar-end--sort select[name="sort_by"]');
    if (!(select instanceof HTMLSelectElement)) return;

    const sortBy = searchParams.get('sort_by');
    if (!sortBy || select.value === sortBy) return;

    const hasOption = [...select.options].some((option) => option.value === sortBy);
    if (hasOption) {
      select.value = sortBy;
    }
  };

  #handleBreakpointChange = () => {
    this.#syncFilterToggleUI();
  };

  /**
   * Desktop: X when sidebar open, filter icon when closed.
   * Mobile: always filter icon (opens drawer).
   */
  #syncFilterToggleUI = () => {
    const toggle = this.#filterToggle;
    if (!toggle) return;

    const isMobile = isMobileBreakpoint();
    const sidebarClosed = document.documentElement.classList.contains(FACETS_SIDEBAR_CLOSED_CLASS);
    const sidebarOpen = !sidebarClosed;
    const showCloseIcon = !isMobile && sidebarOpen;

    toggle.setAttribute('aria-expanded', sidebarOpen ? 'true' : 'false');
    toggle.setAttribute('aria-controls', isMobile ? FILTERS_DRAWER_ID : FACETS_SIDEBAR_ID);
    toggle.setAttribute('aria-haspopup', isMobile ? 'dialog' : 'false');

    const filterIcon = toggle.querySelector('[data-icon="filter"]');
    const closeIcon = toggle.querySelector('[data-icon="close"]');

    if (filterIcon instanceof HTMLElement) {
      filterIcon.hidden = showCloseIcon;
    }

    if (closeIcon instanceof HTMLElement) {
      closeIcon.hidden = !showCloseIcon;
    }
  };
}

if (!customElements.get('popular-filters-component')) {
  customElements.define('popular-filters-component', PopularFiltersComponent);
}
